import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { COLORS, RADIUS, SPACING } from "../../shared/theme";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";

const BORDER_SOFT = "#e0dfd8";
const ZONE_SITE_BG = "#f8f7f3";
const ZONE_SITE_BORDER = "#D3D1C7";

/** Couleurs distinctes par lien collecte → ROPA (cyclées). */
const FLUX_LINK_COLORS = [
  "#7c3aed",
  "#0891b2",
  "#c2410c",
  "#15803d",
  "#b45309",
  "#be185d",
  "#4338ca",
  "#0d9488",
  "#a21caf",
  "#b91c1c",
  "#0369a1",
  "#854d0e",
];

function fluxLinkColor(idx) {
  return FLUX_LINK_COLORS[idx % FLUX_LINK_COLORS.length];
}

/** Flèche dessinée en SVG brut (plus fiable que markerEnd sur les courbes). */
const FLUX_ARROW_DEPTH = 17;
const FLUX_ARROW_HALF_H = 9;

/**
 * Mappe une distance en pixels viewport (entre deux getBoundingClientRect) vers l’espace user du SVG.
 * Sous zoom CSS (.ta-content { zoom }), width/height attributs ≠ getBoundingClientRect(svg) → sans ce facteur,
 * les chemins sont calculés trop à droite et la pointe « mord » dans les cartes.
 */
function svgUserScale(viewBoxW, viewBoxH, svgRect) {
  if (!svgRect || svgRect.width <= 0 || svgRect.height <= 0) return { sx: 1, sy: 1 };
  return {
    sx: viewBoxW / svgRect.width,
    sy: viewBoxH / svgRect.height,
  };
}

function initialsFromNom(nom) {
  const parts = String(nom || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "");
  return (parts.join("") || "?").slice(0, 2);
}

function TreatmentLogo({ nom, logoUrl, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const bg = "#0ea5e9";
  if (!logoUrl || failed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: RADIUS.md,
          background: bg,
          color: "#fff",
          fontSize: Math.max(11, size * 0.35),
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initialsFromNom(nom)}
      </div>
    );
  }
  return (
    <img
      src={logoUrl}
      alt=""
      width={size}
      height={size}
      style={{ borderRadius: RADIUS.md, objectFit: "contain", background: "#f8fafc" }}
      onError={() => setFailed(true)}
    />
  );
}

/** Détail infobulle fiche traitement */
function buildTooltipLines(t, collectesLiees = []) {
  const lines = [];
  lines.push(`Traitement : ${t.nom}`);
  lines.push(`Finalité (catégorie registre) : ${t.finalite || "—"}`);
  lines.push(`Base légale : ${t.baseLegale || "—"}`);
  lines.push("");
  lines.push("Données catégories déclarées :");
  lines.push((Array.isArray(t.categoriesDonnees) && t.categoriesDonnees.length ? t.categoriesDonnees.join(", ") : "—"));
  lines.push("");
  lines.push("Personnes concernées :");
  lines.push((Array.isArray(t.categoriesPersonnes) && t.categoriesPersonnes.length ? t.categoriesPersonnes.join(", ") : "—"));
  lines.push("");
  lines.push("Origines / collecte :");
  lines.push(t.originesDonnees?.trim() || "—");
  lines.push("");
  lines.push("Vers (outils, sous-traitants déclarés dans la fiche) :");
  lines.push(t.sousTraitantsConcernes?.trim() || "—");
  if (Array.isArray(t.destinataires) && t.destinataires.length > 0) {
    lines.push(`Destinataires (types) : ${t.destinataires.join(", ")}`);
  }
  lines.push("");
  lines.push(`Transferts hors UE : ${t.transfertsHorsUE ? "oui" : "non"}`);
  if (t.transfertsHorsUE && (t.transfertsDetail || "").trim()) {
    lines.push(`Détail transferts : ${t.transfertsDetail.trim()}`);
  }
  lines.push("");
  lines.push("Relation au serveur (plateforme) :");
  lines.push(
    "Les fiches sont synchronisées avec le serveur via l’API ; ce sont les données déclarées pour votre organisation, pas une capture réseau temps réel."
  );
  if (t.hapivotPrestataireId) {
    lines.push("");
    lines.push(
      t.hapivotType === "responsable_conjoint"
        ? "Bibliothèque : responsabilité conjointe (art. 26)."
        : "Bibliothèque : sous-traitance / prestataire (art. 28)."
    );
  }
  lines.push("");
  lines.push("Points de collecte liés (module Consentements) :");
  if (!collectesLiees.length) {
    lines.push("— Aucun point relié à ce traitement (champ « Lien vers traitement ROPA »).");
  } else {
    collectesLiees.forEach((p) => {
      const mods = Array.isArray(p.finalites) && p.finalites.length ? p.finalites.join(", ") : "";
      lines.push(
        `· ${p.nom || "Sans nom"} — ${p.type || "—"}${mods ? ` · ${mods}` : ""}`
      );
    });
  }
  return lines;
}

function groupTraitementsByFinalite(traitements) {
  const map = new Map();
  for (const t of traitements || []) {
    const key = (t.finalite && String(t.finalite).trim()) || "Autre";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "fr"));
}

function normalizeTraitementRef(s) {
  return String(s ?? "").trim().toLowerCase();
}

/** Aligné sur le champ « Lien vers traitement ROPA » des points de collecte (= nom du traitement dans le registre). */
function collectesLieesAuTraitement(traitementNom, pointsCollecte = []) {
  const target = normalizeTraitementRef(traitementNom);
  if (!target) return [];
  return (pointsCollecte || []).filter((p) => normalizeTraitementRef(p.traitementROPA) === target);
}

function cardStyle(t) {
  if (t.hapivotType === "responsable_conjoint") {
    return { bg: "#FAEEDA", border: "#854F0B", chip: "Art. 26 · RC", chipColor: "#633806" };
  }
  if (t.hapivotPrestataireId || t.hapivotType === "onboarding_wizard") {
    return {
      bg: "#E6F1FB",
      border: "#185FA5",
      chip: t.hapivotType === "onboarding_wizard" ? "Assistant onboarding" : "Art. 28",
      chipColor: "#0C447C",
    };
  }
  return { bg: "#fff", border: "#CBD5E1", chip: "", chipColor: COLORS.textSecondary };
}

function shortDataPreview(t) {
  const cats = Array.isArray(t.categoriesDonnees) ? t.categoriesDonnees.filter(Boolean) : [];
  if (cats.length === 0) return "Données — à préciser dans le registre";
  return cats.slice(0, 4).join(" · ") + (cats.length > 4 ? "…" : "");
}

/** Flèche horizontale + zone hover pour infobulle « relation flux » */
function FluxArrow({ markerId, tipLines, stroke = "#1D9E75", dashed = false, onShowTip, onMoveTip, onHideTip }) {
  return (
    <div
      style={{
        flex: "0 0 52px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "stretch",
        minHeight: 72,
        cursor: "help",
        position: "relative",
        zIndex: 1,
      }}
      onMouseEnter={(e) => onShowTip(e, tipLines.join("\n"))}
      onMouseMove={onMoveTip}
      onMouseLeave={onHideTip}
    >
      <svg width="48" height="28" viewBox="0 0 48 28" aria-hidden style={{ flexShrink: 0 }}>
        <defs>
          <marker id={markerId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0 0 L8 4 L0 8 Z" fill={stroke} />
          </marker>
        </defs>
        <path
          d="M2 14 H42"
          stroke={stroke}
          strokeWidth="1.5"
          fill="none"
          strokeDasharray={dashed ? "5 4" : undefined}
          markerEnd={`url(#${markerId})`}
        />
      </svg>
    </div>
  );
}

export default function PageCarteFluxTraitements({
  traitements = [],
  pointsCollecte = [],
  organizationName,
  onOpenRegistre,
}) {
  const uid = useId().replace(/:/g, "");
  const scrollCanvasRef = useRef(null);
  const innerFlowRef = useRef(null);
  /** Ancrage départ : carte point de collecte (bord droit). */
  const fluxSrcRefs = useRef(new Map());
  /** Ancrage vertical arrivée : ligne « Collecte » sur la fiche ROPA. */
  const fluxCollecteAnchorsRef = useRef(new Map());
  /** Mesure l’origine réelle du calque connecteurs (voir overlayViewportOrigin). */
  const connectorForegroundRef = useRef(null);
  const [tip, setTip] = useState({ text: "", x: 0, y: 0, visible: false });
  const [pulseTraitementId, setPulseTraitementId] = useState(null);
  const pulseTimerRef = useRef(null);
  const [connectorOverlay, setConnectorOverlay] = useState({ width: 0, height: 0, paths: [] });

  const pointsRelies = useMemo(() => {
    const rows = (pointsCollecte || [])
      .map((p) => {
        const traitement = (traitements || []).find(
          (t) => normalizeTraitementRef(t?.nom) === normalizeTraitementRef(p?.traitementROPA)
        );
        return traitement ? { point: p, traitement } : null;
      })
      .filter(Boolean);
    rows.sort((a, b) => String(a.point?.nom || "").localeCompare(String(b.point?.nom || ""), "fr"));
    return rows;
  }, [pointsCollecte, traitements]);

  const scrollToTraitement = useCallback((traitementId) => {
    const el = document.getElementById(`flux-traitement-${traitementId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
      setPulseTraitementId(traitementId);
      pulseTimerRef.current = window.setTimeout(() => {
        setPulseTraitementId(null);
        pulseTimerRef.current = null;
      }, 2200);
    }
  }, []);

  const grouped = useMemo(() => groupTraitementsByFinalite(traitements), [traitements]);

  const nomsTraitementsNorm = useMemo(() => {
    const set = new Set();
    for (const t of traitements || []) {
      const k = normalizeTraitementRef(t?.nom);
      if (k) set.add(k);
    }
    return set;
  }, [traitements]);

  const stats = useMemo(() => {
    const list = traitements || [];
    const collectesAuRegistre = (pointsCollecte || []).filter((p) =>
      nomsTraitementsNorm.has(normalizeTraitementRef(p?.traitementROPA))
    );
    return {
      total: list.length,
      categories: grouped.length,
      horsUe: list.filter((t) => t.transfertsHorsUE).length,
      avecBib: list.filter((t) => t.hapivotPrestataireId).length,
      collectesLieesRegistre: collectesAuRegistre.length,
      traitementsAvecCollecte: list.filter((t) => collectesLieesAuTraitement(t.nom, pointsCollecte).length > 0).length,
    };
  }, [traitements, grouped.length, pointsCollecte, nomsTraitementsNorm]);

  const showTip = useCallback((e, rawText) => {
    const text =
      typeof rawText === "string"
        ? rawText
        : buildTooltipLines(rawText, collectesLieesAuTraitement(rawText?.nom, pointsCollecte)).join("\n");
    setTip({
      visible: true,
      text,
      x: e.clientX + 14,
      y: e.clientY - 10,
    });
  }, [pointsCollecte]);

  const moveTip = useCallback((e) => {
    setTip((prev) => (prev.visible ? { ...prev, x: e.clientX + 14, y: e.clientY - 10 } : prev));
  }, []);

  const hideTip = useCallback(() => {
    setTip((p) => ({ ...p, visible: false }));
  }, []);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    };
  }, []);

  const pointConnectorKey = useCallback((p, idx) => `${String(p?.id ?? "noid")}__${idx}`, []);

  const updateConnectorOverlay = useCallback(() => {
    const inner = innerFlowRef.current;
    if (!inner || pointsRelies.length === 0) {
      setConnectorOverlay({ width: 0, height: 0, paths: [] });
      return;
    }
    const width = Math.max(inner.scrollWidth, inner.offsetWidth);
    const height = Math.max(inner.scrollHeight, inner.offsetHeight);
    const fg = connectorForegroundRef.current;
    const svgRect = fg?.getBoundingClientRect?.();

    let originLeft;
    let originTop;
    let scaleX = 1;
    let scaleY = 1;

    if (svgRect && svgRect.width > 0 && svgRect.height > 0) {
      originLeft = svgRect.left;
      originTop = svgRect.top;
      const sc = svgUserScale(width, height, svgRect);
      scaleX = sc.sx;
      scaleY = sc.sy;
    } else {
      const ir = inner.getBoundingClientRect();
      originLeft = ir.left + inner.clientLeft;
      originTop = ir.top + inner.clientTop;
      const sc = svgUserScale(width, height, ir);
      scaleX = sc.sx;
      scaleY = sc.sy;
    }

    const paths = [];
    pointsRelies.forEach(({ point: p, traitement: tr }, idx) => {
      const connKey = pointConnectorKey(p, idx);
      const srcCardEl = fluxSrcRefs.current.get(connKey);
      const cardEl = document.getElementById(`flux-traitement-${tr.id}`);
      if (!srcCardEl || !cardEl) return;

      const rOuter = srcCardEl.getBoundingClientRect();
      const rCard = cardEl.getBoundingClientRect();

      /** Départ : bord droit carte collecte (+ léger déport vers la marge pour cohérence avec la pastille). */
      const srcEgressInsetPx = 2;
      const sx = (rOuter.right - originLeft + srcEgressInsetPx) * scaleX;
      const sy = (rOuter.top + rOuter.height / 2 - originTop) * scaleY;

      /**
       * Arrivée : bord gauche carte → coords user ; petite marge avant la ligne pour le trait du polygone.
       * Pointe orientée vers la carte : sommet à droite au niveau du bord extérieur gauche (zone entre colonnes).
       */
      const edgeDistPx = rCard.left - originLeft;
      const tipViewportGapPx = 3;
      const tipX = (edgeDistPx - tipViewportGapPx) * scaleX;
      const stemX = tipX - FLUX_ARROW_DEPTH;

      const collecteEl = fluxCollecteAnchorsRef.current.get(tr.id);
      const tipY = collecteEl
        ? (collecteEl.getBoundingClientRect().top + collecteEl.getBoundingClientRect().height / 2 - originTop) * scaleY
        : (rCard.top + rCard.height / 2 - originTop) * scaleY;

      if (!(stemX > sx + 40 && tipX > sx + 55)) return;

      const midX = sx + (stemX - sx) * 0.48;
      const d = `M ${sx} ${sy} C ${midX} ${sy}, ${midX} ${tipY}, ${stemX} ${tipY}`;
      paths.push({
        key: `${connKey}-${tr.id}`,
        d,
        color: fluxLinkColor(idx),
        tipX,
        tipY,
      });
    });
    setConnectorOverlay({ width, height, paths });
  }, [pointsRelies, pointConnectorKey]);

  useLayoutEffect(() => {
    updateConnectorOverlay();
    const outer = scrollCanvasRef.current;
    const inner = innerFlowRef.current;
    const run = () => updateConnectorOverlay();
    outer?.addEventListener("scroll", run, { passive: true });
    window.addEventListener("resize", run);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(run);
      if (inner) ro.observe(inner);
    }
    const t = window.setTimeout(run, 160);
    const raf = window.requestAnimationFrame(run);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
      outer?.removeEventListener("scroll", run);
      window.removeEventListener("resize", run);
      ro?.disconnect();
    };
  }, [updateConnectorOverlay, grouped, pulseTraitementId]);

  /** Recalcul après montage du SVG (première fois paths non vides), sinon origine mesurée trop tôt. */
  useLayoutEffect(() => {
    if (connectorOverlay.paths.length === 0) return;
    const id = window.requestAnimationFrame(() => updateConnectorOverlay());
    return () => window.cancelAnimationFrame(id);
  }, [connectorOverlay.paths.length, updateConnectorOverlay]);

  return (
    <PageShell>
      <PageHeader
        title="Carte des flux — données"
        subtitle={
          organizationName ? (
            <>
              <strong style={{ color: "#475467" }}>{organizationName}</strong> — lecture gauche-droite : périmètre
              organisationnel, puis traitements groupés par <strong>catégorie de finalité</strong> (registre art. 30). Les{" "}
              <strong>points de collecte</strong> reliés via « Lien vers traitement ROPA » (Consentements) apparaissent sur chaque carte et dans l’infobulle.
              Survolez les <strong>flèches</strong> ou une <strong>carte</strong> pour le détail.
            </>
          ) : (
            "Flux déclarés vers les traitements du registre, par finalité. Les points de collecte reliés au registre apparaissent sur les cartes ; survolez flèches et cartes pour le détail."
          )
        }
        actions={
          onOpenRegistre ? (
            <button type="button" className="page-shell__btn page-shell__btn--ghost" onClick={onOpenRegistre}>
              Ouvrir le registre ROPA
            </button>
          ) : null
        }
      />

      <PageContent>
      <div style={{ maxWidth: 1320, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 18px",
            alignItems: "center",
            marginBottom: SPACING.md,
            fontSize: 11,
            color: "#666",
          }}
        >
          <LegDot color="#1D9E75" label="Flux périmètre → registre" />
          <LegDot color="#185FA5" label="Sous-traitance / art. 28" square />
          <LegDot color="#854F0B" label="Resp. conjointe / art. 26" square />
          <LegDot color="#7c3aed" label="Lien consentement → ROPA (bandeau gauche + sortie droite)" square />
          <span style={{ color: "#98a2b3" }}>· Aligné sur le gabarit « carte des flux » Hapivot</span>
        </div>

        {stats.total === 0 ? (
          <div
            style={{
              padding: 44,
              textAlign: "center",
              background: "#fff",
              border: `0.5px solid ${BORDER_SOFT}`,
              borderRadius: 16,
              color: COLORS.textMuted,
              fontSize: 14,
            }}
          >
            Aucun traitement pour cette organisation. Complétez le registre ROPA pour afficher la carte.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: SPACING.lg }}>
              <StatPill label="Traitements" value={stats.total} />
              <StatPill label="Bandes (finalités)" value={stats.categories} />
              <StatPill label="Points collecte → registre" value={stats.collectesLieesRegistre} />
              <StatPill label="Traitements avec collecte liée" value={stats.traitementsAvecCollecte} />
              <StatPill label="Transferts hors UE déclarés" value={stats.horsUe} />
              <StatPill label="Fiches biblio / onboarding" value={stats.avecBib} />
            </div>

            {/* Canvas carte — scroll horizontal + vertical si beaucoup de bandes ou de fiches */}
            <div
              ref={scrollCanvasRef}
              style={{
                background: "#fff",
                border: `0.5px solid ${BORDER_SOFT}`,
                borderRadius: 16,
                padding: "1.25rem",
                maxHeight: "min(78vh, calc(100vh - 200px))",
                overflowX: "auto",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              <div
                ref={innerFlowRef}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "stretch",
                  gap: 0,
                  width: "max-content",
                  minHeight: 280,
                }}
              >
                {connectorOverlay.paths.length > 0 && connectorOverlay.width > 0 ? (
                  <>
                    <svg
                      aria-hidden
                      width={connectorOverlay.width}
                      height={connectorOverlay.height}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        pointerEvents: "none",
                        zIndex: 0,
                        overflow: "visible",
                      }}
                      viewBox={`0 0 ${connectorOverlay.width} ${connectorOverlay.height}`}
                    >
                      {connectorOverlay.paths.map((row) => (
                        <g key={row.key}>
                          <path
                            d={row.d}
                            stroke="#ffffff"
                            strokeWidth={5}
                            fill="none"
                            opacity={0.55}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d={row.d}
                            stroke={row.color}
                            strokeWidth={2.25}
                            fill="none"
                            opacity={0.65}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                      ))}
                    </svg>
                    {/* Pointes au premier plan : visibles sur le bord gauche du bloc ROPA (traits restent en fond). */}
                    <svg
                      ref={connectorForegroundRef}
                      aria-hidden
                      width={connectorOverlay.width}
                      height={connectorOverlay.height}
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        pointerEvents: "none",
                        zIndex: 3,
                        overflow: "visible",
                      }}
                      viewBox={`0 0 ${connectorOverlay.width} ${connectorOverlay.height}`}
                    >
                      {connectorOverlay.paths.map((row) => (
                        <polygon
                          key={`${row.key}-tip`}
                          points={`${row.tipX},${row.tipY} ${row.tipX - FLUX_ARROW_DEPTH},${row.tipY - FLUX_ARROW_HALF_H} ${row.tipX - FLUX_ARROW_DEPTH},${row.tipY + FLUX_ARROW_HALF_H}`}
                          fill={row.color}
                          stroke="#ffffff"
                          strokeWidth={1.5}
                          strokeLinejoin="round"
                        />
                      ))}
                    </svg>
                  </>
                ) : null}
                {/* Zone gauche « site » */}
                <div
                  style={{
                    flex: "0 0 298px",
                    position: "relative",
                    zIndex: 1,
                    background: ZONE_SITE_BG,
                    border: `1px dashed ${ZONE_SITE_BORDER}`,
                    borderRadius: 12,
                    padding: "12px 10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignSelf: "stretch",
                    minHeight: 280,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#5F5E5A", textAlign: "center" }}>Périmètre organisme</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", textAlign: "center", lineHeight: 1.3 }}>
                    {organizationName || "Votre organisation"}
                  </div>
                  <div style={{ fontSize: 10, color: "#64748b", lineHeight: 1.45, textAlign: "center" }}>
                    Trait coloré en <strong>arrière-plan</strong>&nbsp;; la <strong>pointe</strong> arrive au <strong>bord gauche</strong> de la fiche (entre colonnes), au niveau de la ligne{' '}
                    <strong>Collecte ·</strong>. <strong>Pastille colorée</strong> à droite de chaque carte collecte = sortie du lien (le trait peut passer sous le fond beige jusqu’au bord du cadre).
                  </div>

                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#6d28d9",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      marginTop: 4,
                    }}
                  >
                    Points de collecte
                  </div>
                  <div
                    style={{
                      flex: "0 0 auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      paddingRight: 2,
                      overflow: "visible",
                    }}
                  >
                    {pointsRelies.length === 0 ? (
                      <div
                        style={{
                          fontSize: 10,
                          color: "#94a3b8",
                          fontStyle: "italic",
                          lineHeight: 1.45,
                          padding: "8px 6px",
                          background: "rgba(255,255,255,0.65)",
                          borderRadius: 8,
                          border: `1px dashed ${ZONE_SITE_BORDER}`,
                        }}
                      >
                        Aucun point relié à une fiche ROPA. Dans Consentements, renseignez « Lien vers traitement ROPA » avec le nom exact du traitement.
                      </div>
                    ) : (
                      pointsRelies.map(({ point: p, traitement: tr }, idx) => {
                        const connKey = pointConnectorKey(p, idx);
                        return (
                        <div
                          key={p.id ?? `${p.nom}-${tr.id}-${idx}`}
                          ref={(el) => {
                            if (el) fluxSrcRefs.current.set(connKey, el);
                            else fluxSrcRefs.current.delete(connKey);
                          }}
                          style={{
                            position: "relative",
                            background: "#fff",
                            borderRadius: 10,
                            border: "1px solid #ddd6fe",
                            borderLeft: `3px solid ${fluxLinkColor(idx)}`,
                            padding: "8px 8px 10px",
                            boxShadow: "0 1px 4px rgba(124,58,237,0.06)",
                          }}
                        >
                          <div
                            aria-hidden
                            title="Sortie du lien vers le registre (même couleur que le trait)"
                            style={{
                              position: "absolute",
                              right: -5,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 11,
                              height: 11,
                              borderRadius: "50%",
                              background: fluxLinkColor(idx),
                              border: "2px solid #fff",
                              boxShadow: "0 0 0 1px rgba(15,23,42,0.12)",
                              zIndex: 2,
                              pointerEvents: "none",
                            }}
                          />
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#4c1d95", lineHeight: 1.25 }}>{p.nom || "Sans nom"}</div>
                          <div style={{ fontSize: 9, color: "#64748b", marginTop: 3 }}>
                            {(p.type || "—") + " · " + (p.modaliteRecueil || "—")}
                          </div>
                          <div
                            title="Origine du lien vers la fiche ROPA"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              marginTop: 8,
                              flexWrap: "wrap",
                              borderRadius: 8,
                              padding: "2px 4px 2px 2px",
                              marginLeft: -2,
                              marginRight: -2,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => scrollToTraitement(tr.id)}
                              title={`Afficher la fiche « ${tr.nom} » dans le registre (carte)`}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                textAlign: "left",
                                border: "none",
                                background: "#f5f3ff",
                                color: "#5b21b6",
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: "pointer",
                                padding: "6px 8px",
                                borderRadius: 8,
                                lineHeight: 1.3,
                              }}
                            >
                              {tr.nom}
                            </button>
                          </div>
                          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 6 }}>
                            Finalité registre · {tr.finalite || "—"}
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      paddingTop: 10,
                      borderTop: `1px dashed ${ZONE_SITE_BORDER}`,
                      fontSize: 10,
                      color: "#94a3b8",
                      textAlign: "center",
                      lineHeight: 1.4,
                    }}
                  >
                    {stats.total} traitement(s) · {stats.collectesLieesRegistre} lien(s) collecte→registre
                  </div>
                </div>

                <FluxArrow
                  markerId={`${uid}-a0`}
                  tipLines={[
                    "Relation périmètre organisme → registre des traitements",
                    "",
                    "À gauche : points de collecte reliés au registre — pastille à droite de la carte = sortie du lien ; le trait coloré rejoint la fiche ROPA (cliquez le nom pour centrer la fiche).",
                    "À droite : traitements groupés par finalité (art. 30). Représentation déclarative — pas une capture réseau.",
                  ]}
                  stroke="#1D9E75"
                  onShowTip={showTip}
                  onMoveTip={moveTip}
                  onHideTip={hideTip}
                />

                {grouped.map(([finalite, items], bandIdx) => (
                  <div
                    key={finalite}
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "stretch",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {/* Même gabarit qu’anciennes flèches grises entre finalités, sans ligne ni infobulle. */}
                    {bandIdx > 0 ? (
                      <div
                        aria-hidden
                        style={{ flex: "0 0 52px", minWidth: 52, flexShrink: 0, alignSelf: "stretch" }}
                      />
                    ) : null}
                    <div
                      style={{
                        flex: "0 0 auto",
                        width: 268,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "#64748b",
                          paddingBottom: 8,
                          borderBottom: `2px solid ${bandIdx % 2 === 0 ? "#e2e8f0" : "#cbd5e1"}`,
                        }}
                      >
                        {finalite}
                        <span style={{ fontWeight: 500, color: "#94a3b8", marginLeft: 8 }}>({items.length})</span>
                      </div>

                      {items.map((t) => {
                        const cs = cardStyle(t);
                        const liees = collectesLieesAuTraitement(t.nom, pointsCollecte);
                        return (
                          <div
                            key={t.id}
                            id={`flux-traitement-${t.id}`}
                            role="group"
                            onMouseEnter={(e) => showTip(e, t)}
                            onMouseMove={moveTip}
                            onMouseLeave={hideTip}
                            style={{
                              background: cs.bg,
                              border: `1.5px solid ${pulseTraitementId === t.id ? "#7c3aed" : cs.border}`,
                              borderRadius: 10,
                              padding: "12px 12px 14px",
                              boxShadow:
                                pulseTraitementId === t.id
                                  ? "0 0 0 3px rgba(124,58,237,0.35), 0 4px 14px rgba(124,58,237,0.15)"
                                  : "0 2px 10px rgba(15,23,42,0.06)",
                              cursor: "default",
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                              minHeight: 118,
                              transition: "box-shadow 0.25s ease, border-color 0.25s ease",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                              <TreatmentLogo nom={t.nom} logoUrl={t.logoUrl} size={38} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a", lineHeight: 1.25 }}>{t.nom}</div>
                                {cs.chip ? (
                                  <div style={{ fontSize: 10, fontWeight: 700, color: cs.chipColor, marginTop: 5 }}>{cs.chip}</div>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.45 }}>{shortDataPreview(t)}</div>
                            <div
                              ref={(el) => {
                                const tid = t.id;
                                if (el) fluxCollecteAnchorsRef.current.set(tid, el);
                                else fluxCollecteAnchorsRef.current.delete(tid);
                              }}
                              style={{ fontSize: 10, lineHeight: 1.45 }}
                            >
                              {liees.length > 0 ? (
                                <span style={{ color: "#6d28d9" }}>
                                  <strong style={{ color: "#5b21b6" }}>Collecte · </strong>
                                  {liees.length} point{liees.length > 1 ? "s" : ""}
                                  {": "}
                                  {liees
                                    .map((p) => p.nom || "Sans nom")
                                    .slice(0, 3)
                                    .join(", ")}
                                  {liees.length > 3 ? "…" : ""}
                                </span>
                              ) : (
                                <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                                  Collecte — aucun point relié (Consentements · lien ROPA)
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#64748b",
                                lineHeight: 1.4,
                                paddingTop: 6,
                                marginTop: "auto",
                                borderTop: `1px solid rgba(148,163,184,0.35)`,
                              }}
                            >
                              {(t.sousTraitantsConcernes || "").trim() ? (
                                <>
                                  <span style={{ fontWeight: 600, color: "#334155" }}>Vers · </span>
                                  {(t.sousTraitantsConcernes || "").slice(0, 110)}
                                  {(t.sousTraitantsConcernes || "").length > 110 ? "…" : ""}
                                </>
                              ) : (
                                <span style={{ fontStyle: "italic", color: "#94a3b8" }}>Prestataires / outils — à renseigner</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {tip.visible && (
        <div
          style={{
            position: "fixed",
            left: tip.x,
            top: tip.y,
            zIndex: 10050,
            maxWidth: 320,
            padding: "10px 12px",
            background: "#1a1a1a",
            color: "#fff",
            fontSize: 11,
            lineHeight: 1.55,
            borderRadius: RADIUS.md,
            pointerEvents: "none",
            whiteSpace: "pre-wrap",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}
        >
          {tip.text}
        </div>
      )}
      </PageContent>
    </PageShell>
  );
}

function LegDot({ color, label, dashed, square }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: square ? 10 : 22,
          height: square ? 10 : 3,
          borderRadius: square ? 2 : 0,
          background: dashed ? "transparent" : color,
          border: dashed ? `2px dashed ${color}` : "none",
          flexShrink: 0,
        }}
      />
      <span>{label}</span>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `0.5px solid ${BORDER_SOFT}`,
        borderRadius: RADIUS.md,
        padding: "8px 14px",
        fontSize: 12,
        color: COLORS.textMuted,
      }}
    >
      <strong style={{ fontSize: 18, fontWeight: 600, color: "#534AB7", display: "block" }}>{value}</strong>
      {label}
    </div>
  );
}
